import React, { useEffect, useState } from "react";

import { DatasetDataApi } from "api/DatasetDataApi";
import { useGetDataInfoRange } from "api/api-hooks/datasets-hooks";
import { UpdateDatasetData } from "api/model/requests";
import { InputNumber } from "primereact/inputnumber";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { T } from "services/lang/LanguageService";
import { InputSwitch } from "primereact/inputswitch";
import { ChronoUnitLegacy, truncateDate } from "utils/time-utils";
import CalendarWrapper from "../../../basic/calendar-wrapper/CalendarWrapper";
import { ICombinedData, IDataSetConfig } from "../common/api/interfaces/interfaces";
import { getExtractedDataValue, remakeArrayIntoOtherInterface } from "../utils";
import "./DataInputOutputUpdateTable.scss";

type DataInputOutputUpdateTableProps = {
  identifiers: Array<string>;
  firstLoadDate: Date;
  datasets: Array<IDataSetConfig>;
  dateFrequency: ChronoUnitLegacy;
  titleColumnReader(rowData: ICombinedData): JSX.Element | undefined;
  closeDialog(): void;
};

const DataInputOutputUpdateTable: React.FC<DataInputOutputUpdateTableProps> = ({
  identifiers,
  titleColumnReader,
  firstLoadDate,
  datasets,
  dateFrequency,
  closeDialog,
}) => {
  const [dataQuery, setDataQuery] = useState<Array<any>>([]);
  const [isAllInputsInRange, setIsAllInputsInRange] = useState<boolean>(true);
  const [date, setDate] = useState<Date>(truncateDate(firstLoadDate || new Date(), dateFrequency));

  const fetchQuery = useGetDataInfoRange(
    identifiers.filter(identifier => identifier.length > 0),
    { timeStart: date.toISOString() || firstLoadDate.toISOString() },
    {
      enabled: date && identifiers.length > 0,
    }
  );

  const validateInputs = () => {
    let isValid = true;

    dataQuery.forEach(rowData => {
      if (rowData.type.toUpperCase() === "NUMBER" || rowData.type === undefined) {
        const value = getExtractedDataValue(rowData.data, rowData.decimalPlaces);
        const datasetConfig = datasets.find(d => d.identifier === rowData.identifier);
        if (
          datasetConfig?.valueRange &&
          datasetConfig?.valueRange[0] !== null &&
          datasetConfig?.valueRange[1] !== null
        ) {
          const [min, max] = datasetConfig.valueRange;
          if (value < min || value > max) {
            isValid = false;
          }
        }
      }
    });

    setIsAllInputsInRange(isValid);
  };

  useEffect(() => {
    validateInputs();
  }, [dataQuery]);

  useEffect(() => {
    if (
      fetchQuery.data &&
      !fetchQuery.isError &&
      !fetchQuery.isLoading &&
      fetchQuery.data?.length > 0
    ) {
      const newArrForRemake = identifiers.map(identifier => {
        for (let i = 0; i < fetchQuery.data.length; i++) {
          if (fetchQuery.data[i].identifier === identifier) {
            return fetchQuery.data[i];
          }
        }
        return {
          identifier,
          data: [],
        };
      });
      setDataQuery([...remakeArrayIntoOtherInterface(newArrForRemake, datasets)]);
    }
  }, [fetchQuery.data]);

  useEffect(() => {
    fetchQuery.refetch();
  }, [date]);

  const updateData = () => {
    const tempArr: Array<UpdateDatasetData | undefined> = dataQuery
      .map(rowData => {
        const tempOb = fetchQuery.data?.find((ob: any) => ob.identifier === rowData.identifier);
        if (tempOb && rowData.data.length > 0 && tempOb.data !== rowData.data) {
          if (tempOb.data.length === 0 && !rowData.data[0].value) {
            return;
          }
          if (tempOb.data.length > 0 && tempOb.data[0].value === rowData.data[0].value) {
            return;
          }

          const objVal: { [k: string]: any } = {};

          let dateString = date.toISOString();
          if (rowData.data.length > 0) {
            dateString = rowData.data[0].key;
          }

          objVal[`${dateString}`] = rowData.data[0].value;

          return {
            identifier: rowData.identifier,
            values: objVal,
          };
        }
      })
      .filter(ob => ob != null);

    if (tempArr.length === 0) {
      easasAlert({ summary: T("alert.no-changes-made"), severity: "warn" });
      return;
    }
    DatasetDataApi.updateData(tempArr as Array<UpdateDatasetData>)
      .then(() => {
        easasAlert({ summary: T("alert.datasets-updated"), severity: "success" });
        closeDialog();
      })
      .catch(er => {
        console.error(er);
        easasAlert({ summary: T("alert.error-while-updating-datasets"), severity: "error" });
        closeDialog();
      });
  };

  const valueBodyInputTemplate = (rowData: ICombinedData) => {
    if (rowData.identifier === "") {
      return null;
    }

    let inputDisabled = false;
    let valueRange: number[] | undefined;
    let numberInputTooltip: string | undefined;

    const invalidBorderStyle = "border-solid border-red-500 border-2 border-round-xl";

    const datasetConfig = datasets.find(d => d.identifier === rowData.identifier);
    if (datasetConfig?.editable === false) {
      inputDisabled = true;
    } else if (
      datasetConfig?.identifier === rowData.identifier &&
      (datasetConfig?.editable === undefined || datasetConfig?.editable === true) &&
      (datasetConfig?.type?.toUpperCase() === "NUMBER" || datasetConfig?.type === undefined) &&
      datasetConfig?.valueRange
    ) {
      valueRange = datasetConfig.valueRange;
      numberInputTooltip =
        valueRange[0] !== null && valueRange[1] !== null
          ? `${T("label.allowed-range")}: [${valueRange[0]} : ${valueRange[1]}]`
          : undefined;
    }

    const value = getExtractedDataValue(rowData.data, rowData.decimalPlaces);
    const isValueInvalid =
      valueRange &&
      valueRange[0] !== null &&
      valueRange[1] !== null &&
      (value < valueRange[0] || value > valueRange[1]);

    if (rowData.type.toUpperCase() === "NUMBER") {
      return (
        <InputNumber
          value={value}
          onChange={e => {
            valueChangeHandler(rowData.identifier as string, e.value);
          }}
          minFractionDigits={rowData.decimalPlaces}
          maxFractionDigits={rowData.decimalPlaces}
          disabled={inputDisabled}
          suffix={rowData.unit && rowData.unit !== " " ? ` ${rowData.unit}` : ""}
          placeholder={rowData.unit ? ` ${rowData.unit}` : ""}
          tooltip={numberInputTooltip}
          className={isValueInvalid ? invalidBorderStyle : ""}
        />
      );
    }
    if (rowData.type.toUpperCase() === "BOOLEAN") {
      const inputValue = rowData.data[rowData.data.length - 1]?.value
      return (
        <InputSwitch
          checked={inputValue === 1}
          onChange={e => {
            valueChangeHandler(rowData.identifier as string, e.value ? 1 : 0);
          }}
          disabled={inputDisabled}
        />
      );
    }

    if (rowData.type.toUpperCase() === "ENUM") {
      const enums = datasets.filter(e => e.identifier === rowData.identifier)[0].enum;

      if (enums) {
        const dropdownOptions = Object.entries(enums).map(([key, value]) => ({
          label: key,
          value,
        }));

        return (
          <Dropdown
            value={+getExtractedDataValue(rowData.data, 0)}
            options={dropdownOptions}
            onChange={e => {
              valueChangeHandler(rowData.identifier as string, e.value);
            }}
            disabled={inputDisabled}
          />
        );
      }
      return T("message.no-enum-property");
    }

    return <>[unknown type]</>;
  };

  const valueChangeHandler = (identifier: string, newValue: any) => {
    const tempArr = dataQuery.map(item => {
      if (item.identifier === identifier) {
        return {
          ...item,
          data:
            item.data.length > 0
              ? [{ ...item.data[0], value: newValue }]
              : [{ key: `${date.toISOString()}`, value: newValue }],
        };
      }
      return item;
    });

    setDataQuery(tempArr);
  };

  return (
    <>
      {fetchQuery.isError && <>{T("alert.error")}...</>}

      {!fetchQuery.isError && (
        <div className="full-dialog-main-window">
          <div>
            <CalendarWrapper value={date} onChange={setDate} dateFrequency={dateFrequency} />
          </div>

          <DataTable
            value={dataQuery}
            editMode="cell"
            className="editable-cells-table"
            responsiveLayout="scroll"
            scrollHeight="50vh"
            loading={fetchQuery.isLoading}
            size="small"
          >
            <Column
              field="title"
              header={T("word.dataset")}
              body={titleColumnReader}
              style={{ width: "90%" }}
            />
            <Column
              field="unit"
              header={T("word.value")}
              body={valueBodyInputTemplate}
              style={{ minWidth: "8rem" }}
            />
          </DataTable>
          <div className="button-container">
            <Button
              label={T("action.update")}
              icon="pi pi-pencil"
              onClick={updateData}
              disabled={!isAllInputsInRange}
              className="p-button-success p-button-rounded p-button-sm"
            />
            <Button
              label={T("word.cancel")}
              icon="pi pi-times"
              onClick={closeDialog}
              className="p-button-danger p-button-rounded p-button-sm"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default DataInputOutputUpdateTable;
